import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCartStore } from '../../store/useCartStore';
import { cartService } from '../../services/cartService';
import { GlobalErrorHandler } from '../../errors';
import type { Result } from '../../errors/types';
import { MAX_STOCK_LIMIT, clampQuantity } from '../../utils/cartRules';

type UseCartQuantityControlParams = {
  lineRef: string;
  initialQty: number;
  maxQty?: number;
  debounceMs?: number;
  syncGraceMs?: number;
};

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_SYNC_GRACE_MS = 2000;

export function useCartQuantityControl({
  lineRef,
  initialQty,
  maxQty = MAX_STOCK_LIMIT,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  syncGraceMs = DEFAULT_SYNC_GRACE_MS
}: UseCartQuantityControlParams) {
  const touchUiEdit = useCartStore((s) => s.touchUiEdit);
  const incrementActiveEditors = useCartStore((s) => s.incrementActiveEditors);
  const decrementActiveEditors = useCartStore((s) => s.decrementActiveEditors);
  const setUiItemQuantity = useCartStore((s) => s.setUiItemQuantity);

  const [displayQty, setDisplayQty] = useState(initialQty);
  const [inputValue, setInputValue] = useState(initialQty.toString());
  const [localQuantity, setLocalQuantity] = useState(initialQty);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastUserActionTime = useRef(0);
  const debounceTimerRef = useRef<number | null>(null);
  const skipNextBlurSubmitRef = useRef(false);
  const isFocusedRef = useRef(false);

  const sanitizedInitialQty = useMemo(
    () => Math.max(0, Math.min(initialQty, maxQty)),
    [initialQty, maxQty]
  );

  useEffect(() => {
    const checkSync = () => {
      const timeSinceLastAction = Date.now() - lastUserActionTime.current;
      if (timeSinceLastAction > syncGraceMs && sanitizedInitialQty !== displayQty) {
        setDisplayQty(sanitizedInitialQty);
        setInputValue(sanitizedInitialQty.toString());
        setLocalQuantity(sanitizedInitialQty);
      }
    };

    checkSync();

    if (sanitizedInitialQty !== displayQty) {
      const timer = window.setTimeout(checkSync, syncGraceMs + 100);
      return () => window.clearTimeout(timer);
    }
  }, [sanitizedInitialQty, displayQty, syncGraceMs]);

  const submitQuantity = useCallback(
    async (nextQty: number): Promise<Result<void> | undefined> => {
      console.log('[useCartQuantityControl] submitQuantity', { lineRef, nextQty });
      setIsSubmitting(true);
      const result = await cartService.updateLineItem(lineRef, nextQty);
      if (!result.ok) {
        GlobalErrorHandler.handle(result.error, {
          scope: 'cartDrawer',
          source: 'qtyStepper',
          userInitiated: true,
          correlationId: lineRef
        });
      }
      setIsSubmitting(false);
      return result;
    },
    [lineRef]
  );

  const scheduleDebouncedUpdate = useCallback(
    (nextQty: number) => {
      console.log('[useCartQuantityControl] scheduleDebouncedUpdate', { lineRef, nextQty, debounceMs });
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        void submitQuantity(nextQty);
      }, debounceMs);
    },
    [debounceMs, submitQuantity]
  );

  const applyUpdate = useCallback(
    (rawQty: number, immediate: boolean) => {
      console.log('[useCartQuantityControl] applyUpdate', { lineRef, rawQty, immediate });
      const clamped = clampQuantity(rawQty, maxQty);
      setDisplayQty(clamped);
      setInputValue(clamped.toString());
      setLocalQuantity(clamped);
      lastUserActionTime.current = Date.now();
      touchUiEdit();
      setUiItemQuantity(lineRef, clamped);

      if (immediate) {
        if (debounceTimerRef.current !== null) {
          window.clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        void submitQuantity(clamped);
      } else {
        scheduleDebouncedUpdate(clamped);
      }
    },
    [lineRef, maxQty, scheduleDebouncedUpdate, setUiItemQuantity, submitQuantity, touchUiEdit]
  );

  const onIncrease = useCallback(() => applyUpdate(displayQty + 1, false), [applyUpdate, displayQty]);
  const onDecrease = useCallback(() => applyUpdate(displayQty - 1, false), [applyUpdate, displayQty]);

  const onInputChange = useCallback(
    (value: string) => {
      console.log('[useCartQuantityControl] onInputChange', { lineRef, value });
      const rawValue = value.replace(/[^0-9]/g, '');
      setInputValue(rawValue);
      const numeric = clampQuantity(parseInt(rawValue || '0', 10), maxQty);
      setLocalQuantity(Number.isFinite(numeric) ? numeric : localQuantity);
      touchUiEdit();
    },
    [localQuantity, maxQty, touchUiEdit]
  );

  const onFocus = useCallback(() => {
    console.log('[useCartQuantityControl] onFocus', { lineRef });
    isFocusedRef.current = true;
    incrementActiveEditors();
  }, [incrementActiveEditors]);

  const onBlur = useCallback(() => {
    console.log('[useCartQuantityControl] onBlur', { lineRef, skipNextBlur: skipNextBlurSubmitRef.current });
    if (skipNextBlurSubmitRef.current) {
      skipNextBlurSubmitRef.current = false;
      if (isFocusedRef.current) {
        isFocusedRef.current = false;
        decrementActiveEditors();
      }
      return;
    }

    const nextQty = clampQuantity(localQuantity, maxQty);
    if (!Number.isFinite(nextQty)) {
      setInputValue(displayQty.toString());
      setLocalQuantity(displayQty);
      if (isFocusedRef.current) {
        isFocusedRef.current = false;
        decrementActiveEditors();
      }
      return;
    }

    if (nextQty !== displayQty) {
      applyUpdate(nextQty, true);
    } else {
      setInputValue(displayQty.toString());
      setLocalQuantity(displayQty);
    }

    if (isFocusedRef.current) {
      isFocusedRef.current = false;
      decrementActiveEditors();
    }
  }, [applyUpdate, decrementActiveEditors, displayQty, localQuantity, maxQty]);

  const cancelPending = useCallback(() => {
   // console.log('[useCartQuantityControl] cancelPending', { lineRef });
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const prepareForRemove = useCallback(() => {
    console.log('[useCartQuantityControl] prepareForRemove', { lineRef });
    skipNextBlurSubmitRef.current = true;
    cancelPending();
  }, [cancelPending]);

  useEffect(() => () => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  return {
    displayQty,
    inputValue,
    onIncrease,
    onDecrease,
    onInputChange,
    onFocus,
    onBlur,
    isSubmitting,
    cancelPending,
    prepareForRemove
  };
}
